import React, { useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'

import { Button, Dialog, Textarea, Toast, Typography } from '@chilecompra/react-kit/components'
import { checkInputErrorFileMaxSize, useFile, useTextarea } from '@chilecompra/react-kit'

import { onApplyRevokeSanctionThunk, onCloseRevokeDialogSanction } from '../Sanction.actions'
import {
  DeleteFileButton,
  DeleteFileIcon,
  DeleteFileText,
  DialogContent,
  DialogIcon,
  DialogMessage,
  DialogFooter,
  DialogAttachment,
  HiddenFileInput,
  LoadedFileDivider,
  LoadedFileIcon,
  LoadedFileInfo,
  LoadedFileName,
  LoadedFileRow,
  LoadedFileSize,
  SelectFileButton
} from './SanctionDialog.styles'

import { PUBLIC_URL } from '../../../config/settings/environment'

const SANCTION_DETAILS_REVOKE_DIALOG_DICTIONARY = {
  INIT: 'INIT',
  CONFIRM: 'CONFIRM',
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR'
}

/**
 * The Sanction Details Revoke dialog.
 */
const SanctionDetailsRevokeDialog = ({ onRevokeSuccess }) => {
  const dispatch = useDispatch()

  const { revoke, sanctionID } = useSelector(state => state.sanction)

  const [stateRevoke, setStateRevoke] = useState(SANCTION_DETAILS_REVOKE_DIALOG_DICTIONARY.INIT)
  const [isLoading, setLoading] = useState(false)
  const [attachmentRevoke, setAttachamentRevoke] = useState(null)

  const {
    error: justificacionRevokeError,
    value: justificacionRevokeValue,
    setValue: setJustificationRevokeValue,
    onChange: handleChangeJustificationRevoke
  } = useTextarea({
    initialValue: ''
  })

  const {
    error: fileRevokeError,
    setValue: setFileRevokeValue,
    value: fileRevokeValue,
    onChange: handleChangeFileRevoke,
    onReset: handleResetFileRevoke
  } = useFile({
    changeCallback: updatedValue => {
      setAttachamentRevoke({
        type: updatedValue.type,
        name: updatedValue.name ?? 'Sin información',
        date: updatedValue.lastModifiedDate,
        file: updatedValue
      })
    },
    errorCallbacks: [checkInputErrorFileMaxSize(20)],
    initialValue: null
  })

  const fileInputRef = useRef(null)

  const fileSizeMb = fileRevokeValue ? fileRevokeValue.size / (1024 * 1024) : 0
  const isFileLoaded = Boolean(fileRevokeValue) && !fileRevokeError

  const handleBrowseFile = () => {
    if (!isLoading) fileInputRef.current?.click()
  }

  const onNativeFileChange = event => {
    const [selectedFile] = event.target.files
    if (selectedFile) handleChangeFileRevoke([selectedFile])
    event.target.value = ''
  }

  const handleDeleteFile = () => {
    if (isLoading) return
    setFileRevokeValue(null)
    handleResetFileRevoke()
    setAttachamentRevoke(null)
  }

  const handleClose = () => {
    if (isLoading) return

    dispatch(onCloseRevokeDialogSanction())

    setStateRevoke(SANCTION_DETAILS_REVOKE_DIALOG_DICTIONARY.INIT)
    setJustificationRevokeValue('')
    setFileRevokeValue(null)
    handleResetFileRevoke()
  }

  const handleConfirm = () => {
    setStateRevoke(SANCTION_DETAILS_REVOKE_DIALOG_DICTIONARY.CONFIRM)
  }

  const handleAcepted = () => {
    setLoading(true)
    dispatch(
      onApplyRevokeSanctionThunk({
        reboke: {
          id: sanctionID,
          justification: justificacionRevokeValue,
          file: attachmentRevoke.file
        },
        callback: () => {
          setStateRevoke(SANCTION_DETAILS_REVOKE_DIALOG_DICTIONARY.SUCCESS)
          setLoading(false)
          onRevokeSuccess && onRevokeSuccess()
        },
        callbackError: () => {
          setStateRevoke(SANCTION_DETAILS_REVOKE_DIALOG_DICTIONARY.ERROR)
          setLoading(false)
        }
      })
    )
  }

  const isBlock = useMemo(() => {
    return !justificacionRevokeValue || !fileRevokeValue || fileRevokeError
  }, [justificacionRevokeValue, fileRevokeValue, fileRevokeError])

  const inSmallPrint = useMemo(() => {
    return stateRevoke === SANCTION_DETAILS_REVOKE_DIALOG_DICTIONARY.ERROR ||
      stateRevoke === SANCTION_DETAILS_REVOKE_DIALOG_DICTIONARY.SUCCESS
      ? '410px'
      : '520px'
  }, [stateRevoke])

  const renderInitState = () => (
    <>
      <DialogIcon alt="Dejar sin efecto la sanción" src={`${PUBLIC_URL}/assets/images/circle-warning-icon.svg`} />

      <DialogMessage>
        <Typography fontWeight="bold" variant="h4">
          Dejar sin efecto sanción del proveedor
        </Typography>

        <Typography margin="16px 0 0" variant="body2">
          Para dejar sin efecto la sanción debes indicar una justificación y adjunta el acto administrativo que respalda
          la decisión.
        </Typography>

        <Typography margin="16px 0 0" variant="body2">
          Esta modificación quedará registrada en el historial del proveedor
        </Typography>

        <DialogFooter>
          <Button color="default" margin="28px 0 0" variant="contained" onClick={handleClose}>
            Cancelar
          </Button>

          <Button color="primary" margin="28px 0 0" variant="contained" onClick={handleConfirm}>
            Continuar
          </Button>
        </DialogFooter>
      </DialogMessage>
    </>
  )

  const renderConfirmState = () => (
    <>
      <DialogIcon alt="Dejar sin efecto la sanción" src={`${PUBLIC_URL}/assets/images/circle-warning-icon.svg`} />
      <DialogMessage>
        <Typography fontWeight="bold" variant="h4">
          Dejar sin efecto sanción del proveedor
        </Typography>
        <Typography margin="16px 0 27px" variant="body2">
          Para dejar sin efecto esta sanción, debe indicar una justificación y adjuntar el acto administrativo que
          respalda la decisión. Esta acción quedará registrada en el historial del proveedor.
        </Typography>

        {!isFileLoaded ? (
          <>
            <DialogAttachment container alignItems="center">
              <DialogAttachment item sm={7} xs={12} align="left">
                <Typography variant="body2" fontWeight="bold" margin="0 0 4px">
                  Documentación de respaldo
                </Typography>
                <Typography variant="subtitle" color="black1">
                  Archivo permitido: Formato PDF
                </Typography>
                <Typography variant="subtitle" color="black1">
                  Peso máximo: 20mb
                </Typography>
              </DialogAttachment>
              <DialogAttachment item sm={5} xs={12} margin="10px 0 0">
                <SelectFileButton disabled={isLoading} type="button" onClick={handleBrowseFile}>
                  Seleccionar archivo
                </SelectFileButton>
              </DialogAttachment>
            </DialogAttachment>

            {fileRevokeError && fileRevokeValue && (
              <Toast align="left" margin="16px 0 0" severity="error">
                <Typography variant="body2">
                  <strong>Error</strong> El archivo seleccionado supera el límite de 20 MB. Peso actual:{' '}
                  {Math.round(fileSizeMb)} MB. Por favor, selecciona otro archivo.
                </Typography>
              </Toast>
            )}
          </>
        ) : (
          <>
            <Typography align="left" fontWeight="bold" margin="8px 0 0" variant="body2">
              Archivo cargado
            </Typography>
            <LoadedFileDivider />
            <LoadedFileRow>
              <LoadedFileInfo>
                <LoadedFileIcon />
                <LoadedFileName>{fileRevokeValue.name}</LoadedFileName>
                <LoadedFileSize>({fileSizeMb.toFixed(1).replace('.', ',')}MB)</LoadedFileSize>
              </LoadedFileInfo>
              <DeleteFileButton disabled={isLoading} type="button" onClick={handleDeleteFile}>
                <DeleteFileIcon />
                <DeleteFileText>Eliminar</DeleteFileText>
              </DeleteFileButton>
            </LoadedFileRow>
            <LoadedFileDivider />
          </>
        )}

        <HiddenFileInput ref={fileInputRef} accept="application/pdf" type="file" onChange={onNativeFileChange} />

        <Textarea
          disabled={isLoading}
          error={justificacionRevokeError}
          margin="30px 0 0"
          maxCount="300"
          maxLength="300"
          label={
            <Typography variant="caption" align="left">
              Justificación para dejar sin efecto la sanción
            </Typography>
          }
          onChange={handleChangeJustificationRevoke}
          placeholder="Describa aquí el motivo por el cual deja sin efecto la sanción..."
          size="small"
          value={justificacionRevokeValue}
        />

        <Toast severity="info">Una sanción solo puede quedar sin efecto mediante otro acto administrativo.</Toast>

        <DialogFooter>
          <Button color="default" margin="28px 0 0" variant="contained" onClick={handleClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button
            color="primary"
            disabled={isBlock || isLoading}
            loading={isLoading}
            margin="28px 0 0"
            onClick={handleAcepted}
            variant="contained"
          >
            Aceptar
          </Button>
        </DialogFooter>
      </DialogMessage>
    </>
  )

  const renderSuccessState = () => (
    <>
      <DialogIcon alt="Dejar sin efecto la sanción" src={`${PUBLIC_URL}/assets/images/circle-success-icon.svg`} />
      <DialogMessage>
        <Typography fontWeight="bold" variant="h4">
          La sanción quedó correctamente sin efecto.
        </Typography>
        <Typography margin="16px 0 0" variant="body2">
          Esta acción quedará registrada en el historial de sanciones del proveedor.
        </Typography>
        <Typography margin="16px 0 0" variant="body2">
          La calificación del comportamiento se actualizará en un plazo máximo de 5 días hábiles.
        </Typography>
        <Button color="success" margin="28px 0 0" variant="contained" onClick={handleClose}>
          Aceptar
        </Button>
      </DialogMessage>
    </>
  )

  const renderErrorState = () => (
    <>
      <DialogIcon alt="Dejar sin efecto la sanción" src={`${PUBLIC_URL}/assets/images/circle-warning-icon.svg`} />
      <DialogMessage>
        <Typography fontWeight="bold" variant="h4">
          Lo sentimos
        </Typography>
        <Typography margin="16px 0 0" variant="body2">
          Existen unos inconvenientes. Inténtelo nuevamente
        </Typography>
        <Button color="error" margin="28px 0 0" variant="contained" onClick={handleClose}>
          Cerrar
        </Button>
      </DialogMessage>
    </>
  )

  const renderDialogContent = () => {
    switch (stateRevoke) {
      case SANCTION_DETAILS_REVOKE_DIALOG_DICTIONARY.INIT:
        return renderInitState()
      case SANCTION_DETAILS_REVOKE_DIALOG_DICTIONARY.CONFIRM:
        return renderConfirmState()
      case SANCTION_DETAILS_REVOKE_DIALOG_DICTIONARY.SUCCESS:
        return renderSuccessState()
      case SANCTION_DETAILS_REVOKE_DIALOG_DICTIONARY.ERROR:
        return renderErrorState()
      default:
        return renderInitState()
    }
  }

  return (
    <Dialog
      disableBackdropClick
      maxWidth={inSmallPrint}
      onClose={handleClose}
      open={revoke.showDialog}
      verticalAlign="top"
    >
      <DialogContent>{renderDialogContent()}</DialogContent>
    </Dialog>
  )
}

SanctionDetailsRevokeDialog.propTypes = {
  onRevokeSuccess: PropTypes.func
}

export default SanctionDetailsRevokeDialog
